"use client";

import { Reveal } from "@/components/motion/reveal";
import { SpotlightCard } from "@/components/aceternity/spotlight-card";

/**
 * Feature showcase. Four tactile glass cards, each covering one pillar of the
 * product. Cards emerge on scroll (Reveal) and lift/glow on hover — the hover
 * spring is what makes them feel touchable rather than static panels.
 */

type Feature = {
  tag: string;
  title: string;
  body: string;
  glyph: string;
};

const FEATURES: Feature[] = [
  {
    tag: "AUTO MODE",
    title: "You decide how much it handles",
    body:
      "Start with recommendations only, then let SHERIFF take care of routine responses on its own as your confidence grows. Every automated action is logged, explained, and reversible.",
    glyph: "⣿",
  },
  {
    tag: "TRUST ENGINE",
    title: "Every device earns its trust",
    body:
      "Each device carries a live 0–100 trust score that controls its network access automatically. Risky behaviour tightens access; good behaviour opens it back up.",
    glyph: "◈",
  },
  {
    tag: "LEARNING PERIOD",
    title: "It watches before it acts",
    body:
      "New installations start in a quiet learning period: SHERIFF observes real activity and proves its accuracy to you before anything is ever blocked. You flip the switch when you're ready.",
    glyph: "◐",
  },
  {
    tag: "DETECTION",
    title: "Five layers of defence",
    body:
      "Five independent detection layers, each covering the others' blind spots — if one layer misses a threat, the next one catches it. Every alert is explained in plain English.",
    glyph: "≣",
  },
  {
    tag: "IN-BUILT FIREWALL",
    title: "Blocks malicious sites and addresses",
    body:
      "Dangerous sites and addresses are blocked automatically, system-wide — across every browser and app, including private windows — with no extra hardware to buy. Threats are enriched with global reputation intelligence as they appear.",
    glyph: "⛨",
  },
];

export function Features() {
  return (
    <section id="features" className="relative mx-auto w-full max-w-6xl px-6 py-20">
      <Reveal>
        <div className="mb-12 max-w-2xl">
          <span className="mono-label text-accent-text">capability grid</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            One connected workflow, not five disconnected tools.
          </h2>
          <p className="mt-4 text-lg text-muted">
            Monitoring, trust, and response — one connected system that watches
            your devices, explains every alert in plain English, and guides you
            through fixing it.
          </p>
        </div>
      </Reveal>

      {/* Crypto-ref layout: numbered cards, one highlighted accent card whose
          fill + dark ink make it the focal point of the grid. */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => {
          const hot = f.tag === "IN-BUILT FIREWALL";
          return (
            <Reveal key={f.tag} index={i}>
              <SpotlightCard
                index={i}
                className={
                  hot
                    ? "group block h-full rounded-xl2 p-7 !border-transparent"
                    : "group block h-full rounded-xl2 p-7"
                }
                style={
                  hot
                    ? { background: "var(--accent)", color: "var(--accent-ink)" }
                    : undefined
                }
              >
                <div className="flex items-start justify-between">
                  <span
                    aria-hidden
                    className="text-2xl font-bold tracking-tight"
                    style={{ opacity: hot ? 0.9 : 1, color: hot ? "var(--accent-ink)" : "var(--accent-text)" }}
                  >
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <span
                    className="mono-label"
                    style={{ color: hot ? "var(--accent-ink)" : "var(--muted)", opacity: hot ? 0.75 : 1 }}
                  >
                    {f.tag}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-semibold tracking-tight" style={hot ? { color: "var(--accent-ink)" } : undefined}>
                  {f.title}
                </h3>
                <p
                  className="mt-3 text-base leading-relaxed"
                  style={{ color: hot ? "color-mix(in srgb, var(--accent-ink) 82%, transparent)" : "var(--muted)" }}
                >
                  {f.body}
                </p>
              </SpotlightCard>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
